// frontend/src/modules/marginCenter/services/pricingDecisionService.ts

import axiosInstance from '../../../config/axiosInstance';

import type {
  PricingDecisionCustomerSchemaResponse,
  PricingDecisionEvaluationArchiveResponse,
  PricingOptimizationDecisionCommand,
  PricingOptimizationApplicationCommand,
  PricingOptimizationApplicationReadResponse,
  PricingOptimizationApplicationWriteResponse,
  PricingOptimizationDecisionReadResponse,
  PricingOptimizationDecisionWriteResponse,
  PricingDecisionExecutionResponse,
  PricingDecisionOptimizationRequest,
  PricingDecisionOptimizationResponse,
  PricingDecisionPreflightRequest,
  PricingDecisionPreflightResponse,
  PricingDecisionProductSchemaResponse,
} from '../types/pricingDecision';


const PRICING_DECISION_BASE_PATH = '/pricing/decision';


export async function optimizeCustomerRelationship(
  customerId: string,
  productId: string,
  request: PricingDecisionOptimizationRequest = {},
): Promise<PricingDecisionOptimizationResponse> {
  const { data } =
    await axiosInstance.post<PricingDecisionOptimizationResponse>(
      `${PRICING_DECISION_BASE_PATH}/customers/${
        encodeURIComponent(customerId)
      }/relationships/${
        encodeURIComponent(productId)
      }/optimize`,
      request,
    );

  return data;
}

export async function preflightPricingDecision(
  request: PricingDecisionPreflightRequest,
): Promise<PricingDecisionPreflightResponse> {
  const { data } =
    await axiosInstance.post<PricingDecisionPreflightResponse>(
      `${PRICING_DECISION_BASE_PATH}/optimization/preflight`,
      request,
    );

  return data;
}


export async function executePricingDecision(
  request: PricingDecisionPreflightRequest,
): Promise<PricingDecisionExecutionResponse> {
  const { data } =
    await axiosInstance.post<PricingDecisionExecutionResponse>(
      `${PRICING_DECISION_BASE_PATH}/optimization/execute`,
      request,
    );

  return data;
}

/** Solo consulta el archivo; nunca invoca el motor ni registra una decisión. */
export async function getPricingDecisionEvaluation(
  optimizationId: string,
): Promise<PricingDecisionEvaluationArchiveResponse> {
  const { data } = await axiosInstance.get<PricingDecisionEvaluationArchiveResponse>(
    `${PRICING_DECISION_BASE_PATH}/optimization/evaluations/${encodeURIComponent(optimizationId)}`,
  );
  return data;
}



/** Lectura y escritura humana explícita; nunca invoca el optimizador. */
export async function getPricingOptimizationDecisions(
  optimizationId: string,
): Promise<PricingOptimizationDecisionReadResponse> {
  const { data } = await axiosInstance.get<PricingOptimizationDecisionReadResponse>(
    `${PRICING_DECISION_BASE_PATH}/optimization/evaluations/${encodeURIComponent(optimizationId)}/decisions`,
  );
  return data;
}

export async function recordPricingOptimizationDecision(
  optimizationId: string,
  command: PricingOptimizationDecisionCommand,
): Promise<PricingOptimizationDecisionWriteResponse> {
  const { data } = await axiosInstance.post<PricingOptimizationDecisionWriteResponse>(
    `${PRICING_DECISION_BASE_PATH}/optimization/evaluations/${encodeURIComponent(optimizationId)}/decisions`,
    command,
  );
  return data;
}

/** Declaración humana de aplicación, separada de la decisión y de transacciones. */
export async function getPricingOptimizationApplications(
  optimizationId: string,
): Promise<PricingOptimizationApplicationReadResponse> {
  const { data } = await axiosInstance.get<PricingOptimizationApplicationReadResponse>(
    `${PRICING_DECISION_BASE_PATH}/optimization/evaluations/${encodeURIComponent(optimizationId)}/applications`,
  );
  return data;
}

export async function recordPricingOptimizationApplication(
  optimizationId: string,
  command: PricingOptimizationApplicationCommand,
): Promise<PricingOptimizationApplicationWriteResponse> {
  const { data } = await axiosInstance.post<PricingOptimizationApplicationWriteResponse>(
    `${PRICING_DECISION_BASE_PATH}/optimization/evaluations/${encodeURIComponent(optimizationId)}/applications`,
    command,
  );
  return data;
}

export async function getPricingDecisionProductSchema():
Promise<PricingDecisionProductSchemaResponse> {
  const { data } =
    await axiosInstance.get<PricingDecisionProductSchemaResponse>(
      '/form-schema/product-attributes',
    );

  return data;
}


export async function getPricingDecisionCustomerSchema():
Promise<PricingDecisionCustomerSchemaResponse> {
  const { data } =
    await axiosInstance.get<PricingDecisionCustomerSchemaResponse>(
      '/form-schema/client-attributes',
    );

  return data;
}